/**
 * Exam Offline Recovery Utility
 * ─────────────────────────────
 * - Saves answers to localStorage as a backup layer (no correct answers stored)
 * - Maintains a sync queue for failed API calls
 * - Auto-syncs the queue when the network is restored
 * - Only stores: questionId, questionNumber, selectedAnswer, isMarkedForReview, isVisited
 */

const LS_BACKUP_PREFIX  = 'exam_backup_';  // key: exam_backup_{attemptId}
const LS_QUEUE_PREFIX   = 'exam_queue_';   // key: exam_queue_{attemptId}
const LS_META_PREFIX    = 'exam_meta_';    // key: exam_meta_{attemptId}

/* ─── Keys ─── */
const backupKey = (attemptId) => `${LS_BACKUP_PREFIX}${attemptId}`;
const queueKey  = (attemptId) => `${LS_QUEUE_PREFIX}${attemptId}`;
const metaKey   = (attemptId) => `${LS_META_PREFIX}${attemptId}`;

/* ─── Safe localStorage helpers ─── */
const lsGet = (key) => {
  try { return JSON.parse(localStorage.getItem(key)); } catch { return null; }
};
const lsSet = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
};
const lsDel = (key) => {
  try { localStorage.removeItem(key); } catch {}
};

/* ─── Backup: save the full answers array (safe fields only) ─── */
export const saveBackup = (attemptId, answers, currentIndex, currentSubject) => {
  if (!attemptId) return;
  const safeAnswers = answers.map(({ questionId, questionNumber, selectedAnswer, isMarkedForReview, isVisited }) => ({
    questionId, questionNumber, selectedAnswer, isMarkedForReview, isVisited
  }));
  lsSet(backupKey(attemptId), safeAnswers);
  lsSet(metaKey(attemptId), { currentIndex, currentSubject, savedAt: Date.now() });
};

/* ─── Backup: load saved answers on page reload ─── */
export const loadBackup = (attemptId) => {
  if (!attemptId) return null;
  const answers = lsGet(backupKey(attemptId));
  const meta    = lsGet(metaKey(attemptId));
  if (!answers || !Array.isArray(answers)) return null;
  return { answers, meta };
};

/* ─── Backup: merge server answers with local backup (local wins for non-null) ─── */
export const mergeWithBackup = (serverAnswers, backupAnswers) => {
  if (!backupAnswers) return serverAnswers;
  const backupMap = new Map(backupAnswers.map(a => [String(a.questionId), a]));
  return serverAnswers.map(sa => {
    const local = backupMap.get(String(sa.questionId));
    if (!local) return sa;
    return {
      ...sa,
      selectedAnswer:    local.selectedAnswer    ?? sa.selectedAnswer,
      isMarkedForReview: local.isMarkedForReview ?? sa.isMarkedForReview,
      isVisited:         local.isVisited         || sa.isVisited,
    };
  });
};

/* ─── Backup: clear after successful submission ─── */
export const clearBackup = (attemptId) => {
  if (!attemptId) return;
  lsDel(backupKey(attemptId));
  lsDel(metaKey(attemptId));
  lsDel(queueKey(attemptId));
};

/* ─── Sync Queue: enqueue a failed save request ─── */
export const enqueueFailedSave = (attemptId, answerData) => {
  if (!attemptId) return;
  const queue = lsGet(queueKey(attemptId)) || [];
  // Deduplicate: replace existing entry for same questionId
  const idx = queue.findIndex(q => q.questionId === answerData.questionId);
  if (idx !== -1) queue[idx] = { ...queue[idx], ...answerData, queuedAt: Date.now() };
  else queue.push({ ...answerData, queuedAt: Date.now() });
  lsSet(queueKey(attemptId), queue);
};

/* ─── Sync Queue: drain and send all queued items ─── */
export const flushSyncQueue = async (attemptId, apiPut) => {
  if (!attemptId) return 0;
  const queue = lsGet(queueKey(attemptId)) || [];
  if (queue.length === 0) return 0;

  let flushed = 0;
  const remaining = [];
  for (const item of queue) {
    try {
      await apiPut(item);
      flushed++;
    } catch {
      remaining.push(item);
    }
  }
  lsSet(queueKey(attemptId), remaining);
  return flushed;
};

/* ─── Bulk save: save ALL answers before submission ─── */
export const bulkSaveBeforeSubmit = async (attemptId, answers, apiPut) => {
  const toSave = answers.filter(a => a.isVisited || a.selectedAnswer);
  let saved = 0;
  for (const ans of toSave) {
    try {
      await apiPut({
        questionId:        ans.questionId,
        selectedAnswer:    ans.selectedAnswer,
        isMarkedForReview: ans.isMarkedForReview,
        isVisited:         ans.isVisited,
      });
      saved++;
    } catch {
      // best-effort — submission will proceed regardless
    }
  }
  return saved;
};
